type MessageHandler = (data: any) => void;

export class RealWebSocketClient {
  private ws: WebSocket | null = null;
  private wsUrl: string;
  private token: string | null = null;
  private reconnectAttempts = 0;
  private maxReconnectDelay = 60000;
  private heartbeatInterval: ReturnType<typeof setInterval> | null = null;
  private listeners: Map<string, Set<MessageHandler>> = new Map();
  private shouldReconnect = true;

  constructor(wsUrl?: string) {
    this.wsUrl =
      wsUrl ||
      (import.meta.env?.VITE_WS_URL as string) ||
      (typeof window !== 'undefined'
        ? `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}`
        : '');
  }

  on(type: string, handler: MessageHandler): () => void {
    if (!this.listeners.has(type)) {
      this.listeners.set(type, new Set());
    }
    this.listeners.get(type)!.add(handler);
    return () => this.listeners.get(type)?.delete(handler);
  }

  private emit(type: string, data: any): void {
    this.listeners.get(type)?.forEach((h) => h(data));
    this.listeners.get('*')?.forEach((h) => h({ type, data }));
  }

  async connect(token: string, wsUrl?: string): Promise<void> {
    this.token = token;
    if (wsUrl) {
      this.wsUrl = wsUrl;
    }
    const base = (this.wsUrl || (import.meta.env?.VITE_WS_URL as string) || '').replace(/\/$/, '');
    const path = base.endsWith('/ws') ? '' : '/ws';
    const url = `${base}${path}?token=${encodeURIComponent(token)}`;

    return new Promise((resolve, reject) => {
      this.ws = new WebSocket(url);
      this.ws.onopen = () => {
        this.reconnectAttempts = 0;
        this.send({ v: 1, type: 'hello', data: { client: 'dashboard', version: '1.0.0' } });
        this.startHeartbeat();
        resolve();
      };
      this.ws.onmessage = (e) => this.handleMessage(e);
      this.ws.onclose = () => {
        this.stopHeartbeat();
        if (this.shouldReconnect) {
          this.scheduleReconnect();
        }
      };
      this.ws.onerror = () => reject(new Error('WebSocket connection failed'));
    });
  }

  private handleMessage(e: MessageEvent): void {
    try {
      const msg = JSON.parse(e.data);
      if (msg.v === 1 && msg.type) {
        this.emit(msg.type, msg.data || {});
      }
    } catch {
      // Non-JSON message (binary frame for RDP/file transfer)
      this.emit('binary', e.data);
    }
  }

  send(msg: { v: number; type: string; data?: any }): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg));
    }
  }

  private startHeartbeat(): void {
    this.heartbeatInterval = setInterval(() => {
      this.send({ v: 1, type: 'heartbeat', data: { ts: Date.now() } });
    }, 30000);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  private scheduleReconnect(): void {
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), this.maxReconnectDelay);
    this.reconnectAttempts++;
    setTimeout(() => {
      if (this.shouldReconnect && this.token) {
        this.connect(this.token).catch(() => {});
      }
    }, delay);
  }

  disconnect(): void {
    this.shouldReconnect = false;
    this.stopHeartbeat();
    this.ws?.close();
    this.ws = null;
  }
}

export const wsClient = new RealWebSocketClient();
